/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for quick list modal functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAiProfiles } from '../api/aiProfilesApi';
import type { AiProfile } from '../api/aiProfilesApi';

export function useQuickList(mode: 'create' | 'edit', id: string | undefined, duplicateId: string | null) {
  const navigate = useNavigate();
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<AiProfile[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      // Load first page
      const response = await getAiProfiles({
        page: 1,
        limit: 100,
        sortBy: 'profile_name',
        sortOrder: 'asc'
      });

      let allProfiles = [...response.data];

      // If we got 100 results, try to load more pages
      if (response.data.length === 100 && response.pagination) {
        const totalPages = response.pagination.totalPages || Math.ceil((response.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) {
          try {
            const nextResponse = await getAiProfiles({
              page,
              limit: 100,
              sortBy: 'profile_name',
              sortOrder: 'asc'
            });
            if (Array.isArray(nextResponse.data)) {
              allProfiles = [...allProfiles, ...nextResponse.data];
            }
          } catch (error) {
            // Continue with what we have
            break;
          }
        }
      }

      setQuickListProfiles(allProfiles);
    } catch (error) {
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListProfiles.length, loadQuickList]);

  // Filter profiles client-side
  const filteredQuickList = useMemo(() => {
    const profiles = quickListProfiles.filter(p => p.id != null);
    if (!quickListSearch.trim()) {
      return profiles;
    }
    const searchLower = quickListSearch.toLowerCase();
    return profiles.filter(
      (profile) =>
        profile.profile_name?.toLowerCase().includes(searchLower) ||
        profile.provider_name?.toLowerCase().includes(searchLower) ||
        profile.provider_type?.toLowerCase().includes(searchLower) ||
        profile.model?.toLowerCase().includes(searchLower)
    );
  }, [quickListProfiles, quickListSearch]);

  const handleItemSelect = useCallback(
    (profile: AiProfile) => {
      if (profile.id) {
        navigate(`/admin/ai-integration/profiles/${profile.id}`);
        setShowQuickList(false);
        setQuickListSearch('');
      }
    },
    [navigate]
  );

  const currentItemId = useMemo(() => {
    if (mode === 'edit' && id && id !== 'new' && !duplicateId) {
      return id;
    }
    return null;
  }, [mode, id, duplicateId]);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId
  };
}

